import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import EmojiGrid, { TrendingEmojis } from './components/EmojiGrid';

function App() {
  const [emojiData, setEmojiData] = useState({});
  const [trendingData, setTrendingData] = useState([]);
  const [trendingSummary, setTrendingSummary] = useState('');
  const [sseConnected, setSseConnected] = useState(false);        // browser ↔ backend
  const [streamConnected, setStreamConnected] = useState(true);   // backend ↔ X API (optimistic default — server overrides on first update)
  const [totalPosts, setTotalPosts] = useState(0);
  const [secondsAgo, setSecondsAgo] = useState(null);
  const lastUpdateRef = useRef(null);
  const buildIdRef = useRef(null); // server build ID — reload on deploy

  // LIVE = both SSE pipe AND X stream are up
  const isLive = sseConnected && streamConnected;

  // Merge new emoji counts into state (never replace entirely — keeps data complete)
  const mergeEmojiData = useCallback((newData) => {
    setEmojiData((prev) => {
      const next = { ...prev };
      Object.entries(newData).forEach(([emoji, count]) => {
        const n = typeof count === 'number' ? count : (count?.count ?? 0);
        if (n > 0) next[emoji] = n;
      });
      return next;
    });
  }, []);

  // SSE connection with exponential backoff + jitter reconnection.
  // Prevents thundering-herd when a task dies and 60K clients reconnect at once.
  useEffect(() => {
    let eventSource = null;
    let reconnectTimer = null;
    let backoff = 1000; // start at 1 s
    let unmounted = false;
    let errorCount = 0; // track consecutive errors without any successful data

    const connect = () => {
      if (unmounted) return;

      const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
      const host = window.location.hostname;
      // In dev mode, the React dev server (port 3000) can't proxy EventSource/SSE,
      // so connect directly to the backend on port 3001.
      const port = process.env.NODE_ENV === 'production'
        ? (window.location.port ? `:${window.location.port}` : '')
        : ':3001';
      const sseUrl = `${protocol}//${host}${port}/events`;

      eventSource = new EventSource(sseUrl);

      eventSource.onopen = () => {
        setSseConnected(true);
        backoff = 1000; // reset on successful connection
        errorCount = 0;
      };

      // Helper: mark connection alive on any successful data
      const markAlive = () => {
        errorCount = 0;
        if (!unmounted) setSseConnected(true);
        lastUpdateRef.current = Date.now();
      };

      // Helper: detect deploy — reload if server buildId advances
      const checkBuildId = (data) => {
        const id = data?.buildId;
        if (!id) return;
        if (buildIdRef.current === null) {
          buildIdRef.current = id; // first value — store as baseline
        } else if (id > buildIdRef.current) {
          console.log(`🔄 New deploy detected (buildId ${buildIdRef.current} → ${id}), reloading…`);
          window.location.reload();
        }
        // id < stored means we hit an old task during rolling deploy — ignore
      };

      // Helper: update stream status from any payload that includes it
      const updateStreamStatus = (data) => {
        if (data && typeof data.streamConnected === 'boolean' && !unmounted) {
          setStreamConnected(data.streamConnected);
        }
        // Also check nested stats object (initial update format)
        const stats = data?.stats || data?.data;
        if (stats && typeof stats.streamConnected === 'boolean' && !unmounted) {
          setStreamConnected(stats.streamConnected);
        }
      };

      // Listen for the server's initial "connected" event
      eventSource.addEventListener('connected', () => {
        markAlive();
      });

      eventSource.addEventListener('update', (event) => {
        try {
          const data = JSON.parse(event.data);
          checkBuildId(data);
          updateStreamStatus(data);

          // Delta update
          if (data.type === 'delta' && Array.isArray(data.deltas)) {
            const updates = {};
            data.deltas.forEach((d) => {
              if (d.emoji != null) {
                const c = d.count ?? 0;
                if (c > 0) updates[d.emoji] = c;
              }
            });
            if (Object.keys(updates).length > 0) mergeEmojiData(updates);
            // Trending + summary ride along on delta messages too
            if (data.trendingEmojis && Array.isArray(data.trendingEmojis)) {
              setTrendingData(data.trendingEmojis);
            }
            if (data.trendingSummary) setTrendingSummary(data.trendingSummary);
            if (data.totalPosts) setTotalPosts(data.totalPosts);
            markAlive();
            return;
          }

          // Full update
          const stats = data.stats || data.data || data;
          if (stats) {
            if (stats.topEmojis && Array.isArray(stats.topEmojis)) {
              const updates = {};
              stats.topEmojis.forEach((item) => {
                if (typeof item === 'object' && item.emoji != null) {
                  const c = item.count ?? 0;
                  if (c > 0) updates[item.emoji] = c;
                }
              });
              if (Object.keys(updates).length > 0) mergeEmojiData(updates);
            }
            if (stats.trendingEmojis && Array.isArray(stats.trendingEmojis)) {
              setTrendingData(stats.trendingEmojis);
            }
            // Grok trending summary — check both top-level and nested stats
            const summary = data.trendingSummary || stats.trendingSummary;
            if (summary) setTrendingSummary(summary);
            if (stats.totalPosts) setTotalPosts(stats.totalPosts);
            markAlive();
          }
        } catch (error) {
          console.error('Error parsing SSE update event:', error);
        }
      });

      // Listen for immediate stream status changes (connect/disconnect)
      eventSource.addEventListener('stream_status', (event) => {
        try {
          const data = JSON.parse(event.data);
          updateStreamStatus(data);
        } catch (error) {
          console.error('Error parsing stream_status event:', error);
        }
      });

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          checkBuildId(data);
          updateStreamStatus(data);
          const stats = data.stats || data.data || data;
          if (!stats) return;
          if (stats.topEmojis && Array.isArray(stats.topEmojis)) {
            const updates = {};
            stats.topEmojis.forEach((item) => {
              if (typeof item === 'object' && item.emoji != null) {
                const c = item.count ?? 0;
                if (c > 0) updates[item.emoji] = c;
              }
            });
            if (Object.keys(updates).length > 0) mergeEmojiData(updates);
          }
          if (stats.trendingEmojis && Array.isArray(stats.trendingEmojis)) {
            setTrendingData(stats.trendingEmojis);
          }
          const summary = data.trendingSummary || stats.trendingSummary;
          if (summary) setTrendingSummary(summary);
          if (stats.totalPosts) setTotalPosts(stats.totalPosts);
          markAlive();
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      eventSource.onerror = () => {
        errorCount++;
        // EventSource fires onerror on transient hiccups AND real disconnects.
        // Only tear down and reconnect manually if the browser gave up
        // (readyState === CLOSED) or we've seen multiple consecutive errors
        // without any successful data in between.
        if (eventSource.readyState === EventSource.CLOSED || errorCount >= 3) {
          setSseConnected(false);
          eventSource.close();
          if (unmounted) return;
          const jitter = backoff * (0.5 + Math.random());
          reconnectTimer = setTimeout(connect, jitter);
          backoff = Math.min(backoff * 2, 10000); // cap at 10s (was 30s)
        }
        // Otherwise let the browser's native auto-reconnect handle it
        // (EventSource reconnects automatically when readyState === CONNECTING)
      };
    };

    connect();

    return () => {
      unmounted = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (eventSource) eventSource.close();
    };
  }, [mergeEmojiData]);

  // Tick every second to update "Xs ago" display + safety-net reload if stuck offline
  useEffect(() => {
    const id = setInterval(() => {
      if (lastUpdateRef.current) {
        const ago = Math.floor((Date.now() - lastUpdateRef.current) / 1000);
        setSecondsAgo(ago);
        // Safety net: if no data for 60s, force reload (recover from stuck states)
        if (ago > 60) {
          console.log('⚠️ No data for 60s — force reloading…');
          window.location.reload();
        }
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Dynamic tab title + favicon with the hottest trending emoji
  useEffect(() => {
    if (!trendingData || trendingData.length === 0) return;

    const hot = trendingData[0]; // already sorted by velocity (hottest first)
    if (!hot || !hot.emoji) return;

    document.title = `Trending — X Emoji Tracker`;

    // Render emoji to a tiny canvas and set as favicon
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.font = '52px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(hot.emoji, 32, 36);
        const url = canvas.toDataURL('image/png');
        let link = document.querySelector('link[rel="icon"]');
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = url;
      }
    } catch (_) {
      // favicon update is best-effort
    }
  }, [trendingData]);

  const fmtCount = (n) => {
    if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
    return n.toLocaleString();
  };

  const fmtAge = (s) => {
    if (s == null) return '';
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m`;
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-row">
          <h1>𝕏 Emoji Tracker</h1>
          <div className="header-meta">
            {totalPosts > 0 && (
              <span className="post-counter">{fmtCount(totalPosts)} posts scanned</span>
            )}
            <span className={`live-badge ${isLive ? 'live' : 'offline'}${secondsAgo != null && secondsAgo > 20 ? ' stale' : ''}`}>
              <span className="live-dot" />
              {isLive ? 'LIVE' : 'OFFLINE'}
              {secondsAgo != null && secondsAgo >= 1 && (
                <span className="freshness"> · {fmtAge(secondsAgo)}</span>
              )}
            </span>
          </div>
        </div>
      </header>

      <main className="App-main">
        <TrendingEmojis trendingData={trendingData} trendingSummary={trendingSummary} />
        <EmojiGrid
          emojiData={emojiData}
          trendingData={trendingData}
          trackedEmojis={[]}
        />
      </main>

      <footer className="App-footer">
        Powered by <a href="https://developer.x.com" target="_blank" rel="noopener noreferrer">𝕏 API</a> & <a href="https://console.x.ai" target="_blank" rel="noopener noreferrer">xAI API</a>
      </footer>
    </div>
  );
}

export default App;
